import AppService from 'App/legacy/AppService';
import OrderRow from 'App/resources/Model/OrderRow';
import { Currency, Tier } from 'App/resources/Model/Product';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';

type PriceListEntry = Currency | Tier;
type PricedProduct = {
	currencies?: Currency[];
	tiers?: Tier[];
	bundle?: { product?: PricedProduct }[];
};
// Salesboard sends a partial order, the edit modal a complete one.
type ScopableOrder = { priceListId?: number; orderRow?: OrderRow[] };

// A product holds one entry per price list per currency, which for customers with many price lists
// dwarfs the order itself. Only the entries for the price lists in play mean anything to a listener.
const scopeToPriceLists = <T extends PriceListEntry>(entries: T[] | undefined, priceListIds: Set<number>) => {
	if (!entries?.length) {
		return entries;
	}

	return entries.filter(entry => entry.priceListId == null || priceListIds.has(entry.priceListId));
};

const scopeProduct = <T extends PricedProduct>(product: T | undefined, priceListIds: Set<number>): T | undefined => {
	if (!product) {
		return product;
	}

	return {
		...product,
		currencies: scopeToPriceLists(product.currencies, priceListIds),
		tiers: scopeToPriceLists(product.tiers, priceListIds),
		// Bundle components are hydrated products in their own right, each with an unfiltered matrix.
		bundle: product.bundle?.map(component => ({
			...component,
			product: scopeProduct(component.product, priceListIds)
		}))
	};
};

const scopeOrder = <T extends ScopableOrder>(order: T, priceListIds: Set<number>): T => ({
	...order,
	// A drag can leave a hole in the row array that cloneDeep turns into an explicit undefined.
	orderRow: order.orderRow?.map(row =>
		row
			? {
					...row,
					product: scopeProduct(row.product, priceListIds),
					bundleRows: row.bundleRows?.map(bundleRow => ({
						...bundleRow,
						product: scopeProduct(bundleRow.product, priceListIds)
					}))
			  }
			: row
	)
});

// The default list prices the row when its own list has no entry, and is the base a discount is shown against.
const getDefaultPriceListId = () => AppService.getPriceLists().find(list => list.isDefault)?.id;

// Shallow copies with product pricing narrowed to the price lists in play, for what leaves the
// browser. Originals are untouched — the edit order modal prices rows off the full matrix.
const scopeOrderPrices = <T extends ScopableOrder, P extends ScopableOrder>(order: T, previousOrder?: P | null) => {
	// Listeners have consumed the full matrix for years, so narrowing rolls out per customer.
	if (!hasSoftDeployAccess('SCOPE_ORDER_INTEGRATION_PRICES')) {
		return { order, previousOrder };
	}

	const priceListIds = new Set(
		[order?.priceListId, previousOrder?.priceListId, getDefaultPriceListId()].filter((id): id is number => !!id)
	);

	// Without a price list there is nothing to narrow against, and such customers have few entries anyway.
	if (!order || !priceListIds.size) {
		return { order, previousOrder };
	}

	return {
		order: scopeOrder(order, priceListIds),
		previousOrder: previousOrder ? scopeOrder(previousOrder, priceListIds) : previousOrder
	};
};

export default scopeOrderPrices;
